import { useState, useCallback, useRef, useEffect } from "react";
import { ScriptSettings } from "./useScripts";
import { countWords } from "@/lib/utils";

interface ScenePreset {
  fontSize: number;
  scrollSpeed: number;
  mirrorMode: boolean;
  showTimer: boolean;
  layout: "fullscreen" | "floating";
}

const SCENE_PRESETS: Record<string, ScenePreset> = {
  recording: {
    fontSize: 64,
    scrollSpeed: 30, // Reduced from 60
    mirrorMode: true,
    showTimer: true,
    layout: "fullscreen",
  },
  speech: {
    fontSize: 56,
    scrollSpeed: 25, // Reduced from 40
    mirrorMode: false,
    showTimer: true,
    layout: "fullscreen",
  },
  meeting: {
    fontSize: 36,
    scrollSpeed: 20, // Reduced from 30
    mirrorMode: false,
    showTimer: false,
    layout: "floating",
  },
  podcast: {
    fontSize: 48,
    scrollSpeed: 25, // Reduced from 50
    mirrorMode: false,
    showTimer: true,
    layout: "fullscreen",
  },
};

interface SmoothScrollElement extends HTMLElement {
    setSmoothScroll?: (position: number, isPlaying: boolean) => void;
}

interface TeleprompterState {
  isPlaying: boolean;
  scrollPosition: number;
  speed: number; // percentage: 50 = 50%, 100 = 100%, 200 = 200%
  fontSize: number;
  mirrorMode: boolean;
  showTimer: boolean;
  showControls: boolean;
  elapsedTime: number;
  isFullscreen: boolean;
  isControlsPinned: boolean;
}

export function useTeleprompter(
  content: string,
  sceneType: string,
  scriptSettings: ScriptSettings
) {
  const preset = SCENE_PRESETS[sceneType] || SCENE_PRESETS.recording;
  
  const [state, setState] = useState<TeleprompterState>({
    isPlaying: false,
    scrollPosition: 0,
    speed: scriptSettings.scrollSpeed || 100,
    fontSize: scriptSettings.fontSize || preset.fontSize,
    mirrorMode: scriptSettings.mirrorMode ?? preset.mirrorMode,
    showTimer: preset.showTimer,
    showControls: true,
    elapsedTime: 0,
    isFullscreen: false,
    isControlsPinned: false,
  });



  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPlayingRef = useRef<boolean>(false);
  const stateRef = useRef(state);

  // Word count and time estimation
  const wordCount = countWords(content);
  // Increase base from 150 to 200 to better suit CJK/Mixed mixed content average
  const baseReadingTime = (wordCount / 200) * 60; // in seconds
  const adjustedReadingTime = baseReadingTime / (state.speed / 100);
  
  // Remaining Time: strictly Total - Elapsed (Stopwatch Logic)
  const remainingTime = Math.max(0, adjustedReadingTime - state.elapsedTime);

  const scrollRef = useRef(state.scrollPosition);
  const elapsedTimeRef = useRef(state.elapsedTime);
  const startTimeRef = useRef<number>(0);
  const lastStateUpdateRef = useRef<number>(0);
  const maxScrollRef = useRef<number>(0);

  // Sync refs with state
  useEffect(() => {
    // Detect Play Start (Resume/Start)
    if (state.isPlaying && !isPlayingRef.current) {
        // Set startTime such that: timestamp - startTime = currentElapsed
        // So: startTime = timestamp - currentElapsed
        startTimeRef.current = performance.now() - (state.elapsedTime * 1000);
    } else if (!state.isPlaying && isPlayingRef.current) {
        // Just Paused/Stopped: Reset to native scrolling to allow user interaction
        if (containerRef.current) {
             const container = containerRef.current as SmoothScrollElement;
             if (container.setSmoothScroll) {
                 container.setSmoothScroll(scrollRef.current, false);
             }
        }
    }
    
    isPlayingRef.current = state.isPlaying;
    if (state.isPlaying) {
        lastTimeRef.current = performance.now();
    }

    stateRef.current = state;
    // Only update refs if not playing (to allow manual adjustment or sync)
    // or if the difference is significant (e.g. reset)
    if (!state.isPlaying) {
       if (Math.abs(scrollRef.current - state.scrollPosition) > 1) {
          scrollRef.current = state.scrollPosition;
       }
       if (Math.abs(elapsedTimeRef.current - state.elapsedTime) > 0.1) {
          elapsedTimeRef.current = state.elapsedTime;
       }
    }
  }, [state]);

  // Optimize maxScroll calculation to prevent layout thrashing
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let attempts = 0;
    
    const setupObserver = () => {
      if (containerRef.current) {
         // Initial update
         const newMax = Math.max(0, containerRef.current.scrollHeight - containerRef.current.clientHeight);
         maxScrollRef.current = newMax;
         
         // Setup observer
         resizeObserver = new ResizeObserver(() => {
           // CRITICAL FIX: Don't update maxScroll while playing to avoid layout thrashing
           // causing premature stopping (e.g. scrollHeight changing due to overflow:hidden)
           if (containerRef.current && !isPlayingRef.current) {
             const obsMax = Math.max(0, containerRef.current.scrollHeight - containerRef.current.clientHeight);
             maxScrollRef.current = obsMax;
             console.log('[useTeleprompter] Observer updated maxScroll:', obsMax);
           }
         });
         resizeObserver.observe(containerRef.current);
      } else if (attempts < 10) {
        // Retry if not ready yet (container ref might take a moment to be populated via useImperativeHandle)
        attempts++;
        setTimeout(setupObserver, 100);
      }
    };

    setupObserver();

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [wordCount, preset.fontSize, state.fontSize, content]);

  // Return cached value with safety fallback
  const getMaxScroll = useCallback(() => {
    // Safety: If cache is 0 (uninitialized) or negative but container exists, force a read.
    // This ensures playability immediately even if observer hasn't attached yet.
    if (maxScrollRef.current <= 0 && containerRef.current) {
       const freshMax = Math.max(0, containerRef.current.scrollHeight - containerRef.current.clientHeight);
       if (freshMax > 0) {
           maxScrollRef.current = freshMax;
       }
       return freshMax;
    }
    return maxScrollRef.current;
  }, []);

  // Animation loop for auto-scroll - uses refs to avoid stale closures
  const animate = useCallback((timestamp: number) => {
    if (!lastTimeRef.current) {
      lastTimeRef.current = timestamp;
    }

    const delta = timestamp - lastTimeRef.current;
    lastTimeRef.current = timestamp;

    // Use ref for isPlaying to get latest value
    if (isPlayingRef.current) {
        // Safe lazy initialization of maxScroll if it hasn't been set yet
        if (maxScrollRef.current === 0 && containerRef.current) {
             maxScrollRef.current = containerRef.current.scrollHeight - containerRef.current.clientHeight;
        }

        const maxScroll = getMaxScroll();
        const speedFactor = stateRef.current.speed / 100;
        const pixelsPerMs = (preset.scrollSpeed / 1000) * speedFactor;
        
        // Update Ref (high precision)
        let newPosition = scrollRef.current + delta * pixelsPerMs;
        
        // Safety: ensure newPosition is not NaN
        if (isNaN(newPosition)) {
            newPosition = scrollRef.current;
        }
        
        // Cap at maxScroll
        newPosition = Math.min(newPosition, maxScroll);
        
        // STOPWATCH MODE: Absolute Time Calculation
        // Robust against frame drops/throttling. 1s = 1s System Clock.
        const newElapsed = (timestamp - startTimeRef.current) / 1000;
        elapsedTimeRef.current = newElapsed;
        
        scrollRef.current = newPosition;

        if (containerRef.current) {
          // Use Hybrid Scrolling (transform-based)
          // Cast to custom interface to access custom method
          const container = containerRef.current as SmoothScrollElement;
          if (container.setSmoothScroll) {
             container.setSmoothScroll(newPosition, true);
          } else {
             // Fallback
             containerRef.current.scrollTop = newPosition;
          }
        }

        // Throttle React State Updates (~10fps) to prevent jitter from re-renders
        if (timestamp - lastStateUpdateRef.current > 100) {
            setState((prev) => ({ 
              ...prev, 
              scrollPosition: newPosition, 
              elapsedTime: newElapsed 
            }));
            lastStateUpdateRef.current = timestamp;
        } else {
          // Still verify end condition
           if (maxScroll > 0 && newPosition >= maxScroll) {
             isPlayingRef.current = false;
             console.log('[useTeleprompter] Reached end. MaxScroll:', maxScroll, 'Pos:', newPosition);
             setState((prev) => ({ ...prev, scrollPosition: maxScroll, isPlaying: false, elapsedTime: newElapsed }));
           }
        }
    }

    // Continue animation loop
    animationRef.current = requestAnimationFrame(animate);
  }, [getMaxScroll, preset.scrollSpeed]);

  // Start animation on mount, stop on unmount
  useEffect(() => {
    // Start the animation loop
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate]);

  // Note: Scroll position is applied in TeleprompterDisplay component normally, 
  // but we also apply it directly in animate for smoothness.

  // Auto-hide controls (desktop only - mobile uses tap-to-dismiss)
  const showControlsTemporarily = useCallback(() => {
    setState((prev) => ({ ...prev, showControls: true }));
    
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }

    if (state.isControlsPinned) return;

    // On touch devices, don't auto-hide - user will tap outside to dismiss
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    // Desktop: auto-hide after 3 seconds if playing
    hideControlsTimeoutRef.current = setTimeout(() => {
      if (stateRef.current.isControlsPinned) return;

      if (stateRef.current.isPlaying) {
        setState((prev) => ({ ...prev, showControls: false }));
      }
    }, 3000);
  }, [state.isControlsPinned]);

  const toggleControlsPinned = useCallback(() => {
    setState((prev) => {
      const newPinned = !prev.isControlsPinned;
      if (newPinned) {
         if (hideControlsTimeoutRef.current) clearTimeout(hideControlsTimeoutRef.current);
         return { ...prev, isControlsPinned: true, showControls: true };
      }
      return { ...prev, isControlsPinned: false };
    });
  }, []);

  const hideControls = useCallback(() => {
    if (state.isControlsPinned) return; // Don't hide if pinned
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    setState((prev) => ({ ...prev, showControls: false }));
  }, [state.isControlsPinned]);






  // Control functions
  const togglePlay = useCallback(() => {
    console.log('[togglePlay] Called');
    console.trace('[togglePlay] Stack trace');
    
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    setState((prev) => {
      const willPlay = !prev.isPlaying;
      
      // AUTO-REWIND CHECK
      const maxScroll = getMaxScroll();
      let startPos = prev.scrollPosition;
      
      // If trying to play but already at end (within 5px tolerance)
      if (willPlay && maxScroll > 0 && prev.scrollPosition >= maxScroll - 5) {
          console.log('[togglePlay] At end, auto-rewinding to start');
          startPos = 0;
          scrollRef.current = 0;
           // We'll update the ref immediately, content will jump in next frame
      }
      
      console.log('[togglePlay] willPlay:', willPlay, 'StartPos:', startPos, 'MaxScroll:', maxScroll);
      
      if (willPlay) {
         // STARTING PLAY
         // Ensure ref starts from the current UI state position (or rewind position)
         scrollRef.current = startPos;
         return { ...prev, isPlaying: true, scrollPosition: startPos };
      } else {
         // PAUSING
         // Snap state to the most recent high-precision ref value
         return { ...prev, isPlaying: false, scrollPosition: scrollRef.current };
      }
    });
  }, [getMaxScroll]);

  const setSpeed = useCallback((speed: number) => {
    setState((prev) => ({ ...prev, speed: Math.max(1, Math.min(1000, speed)) }));
  }, []);

  const adjustSpeed = useCallback((delta: number) => {
    setState((prev) => ({ 
      ...prev, 
      speed: Math.max(1, Math.min(1000, prev.speed + delta)) 
    }));
  }, []);

  const setFontSize = useCallback((size: number) => {
    setState((prev) => ({ ...prev, fontSize: Math.max(24, Math.min(120, size)) }));
  }, []);

  const adjustFontSize = useCallback((delta: number) => {
    setState((prev) => ({ 
      ...prev, 
      fontSize: Math.max(24, Math.min(120, prev.fontSize + delta)) 
    }));
  }, []);

  const toggleMirrorMode = useCallback(() => {
    setState((prev) => ({ ...prev, mirrorMode: !prev.mirrorMode }));
  }, []);

  // For state restoration from persistence
  const setScrollPosition = useCallback((position: number) => {
    setState((prev) => ({ ...prev, scrollPosition: Math.max(0, position) }));
    scrollRef.current = position;
  }, []);

  const setElapsedTime = useCallback((time: number) => {
    setState((prev) => ({ ...prev, elapsedTime: Math.max(0, time) }));
    startTimeRef.current = performance.now() - time * 1000;
  }, []);

  const jumpTime = useCallback((seconds: number) => {
    setState((prev) => {
      const maxScroll = getMaxScroll();
      const pixelsPerSecond = preset.scrollSpeed * (prev.speed / 100);
      const deltaPixels = seconds * pixelsPerSecond;
      const newPosition = Math.max(0, Math.min(prev.scrollPosition + deltaPixels, maxScroll));
      
      // Update ref and DOM immediately
      scrollRef.current = newPosition;
      if (containerRef.current) {
        containerRef.current.scrollTop = newPosition;
      }
      
      return { ...prev, scrollPosition: newPosition };
    });
  }, [getMaxScroll, preset.scrollSpeed]);

  const reset = useCallback(() => {
    scrollRef.current = 0;
    elapsedTimeRef.current = 0;
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    setState((prev) => ({ 
      ...prev, 
      scrollPosition: 0, 
      elapsedTime: 0, 
      isPlaying: false 
    }));
  }, []);



  const manualScroll = useCallback((delta: number) => {
    // If NOT playing, we just adjust the position
    if (!isPlayingRef.current) {
      setState((prev) => {
        const maxScroll = getMaxScroll();
        const newPosition = Math.max(0, Math.min(prev.scrollPosition + delta, maxScroll));
        
        scrollRef.current = newPosition;
        if (containerRef.current) {
          containerRef.current.scrollTop = newPosition;
        }
        return { ...prev, scrollPosition: newPosition };
      });
      return;
    }

    // IF PLAYING: We override the position and RECALCULATE startTimeRef
    // to keep the auto-scroll moving forward from the new position
    // and keep elapsedTime/Progress bar in sync with script position.
    const maxScroll = getMaxScroll();
    let newPosition = scrollRef.current + delta;
    newPosition = Math.max(0, Math.min(newPosition, maxScroll));
    
    scrollRef.current = newPosition;
    
    // Calculate pixels per second to correct the timer
    const speedFactor = stateRef.current.speed / 100;
    const pixelsPerSecond = preset.scrollSpeed * speedFactor;
    
    if (pixelsPerSecond > 0) {
      // Correct startTimeRef: timestamp - startTime = newElapsed
      // newElapsed = newPosition / pixelsPerSecond
      const newElapsed = newPosition / pixelsPerSecond;
      const now = performance.now();
      startTimeRef.current = now - (newElapsed * 1000);
      elapsedTimeRef.current = newElapsed;
      
      // Force an immediate UI state update to keep progress bar in sync
      setState((prev) => ({ 
        ...prev, 
        scrollPosition: newPosition,
        elapsedTime: newElapsed
      }));
    }

    // Direct DOM update for smoothness
    if (containerRef.current) {
      const container = containerRef.current as SmoothScrollElement;
      if (container.setSmoothScroll) {
        container.setSmoothScroll(newPosition, true);
      }
    }
  }, [getMaxScroll, preset.scrollSpeed]);

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setState((prev) => ({ ...prev, isFullscreen: true }));
    } else {
      await document.exitFullscreen();
      setState((prev) => ({ ...prev, isFullscreen: false }));
    }
  }, []);

  // Sync scroll position from external source (e.g. manual scroll in display)
  const syncScrollPosition = useCallback((newPosition: number) => {
    // If playing, completely IGNORE scroll updates from the DOM.
    // This prevents "ghost" events (e.g. from transform hacks setting scrollTop=0)
    // from overwriting our high-precision scrollRef.
    if (isPlayingRef.current) {
        return;
    }

    scrollRef.current = newPosition;
    setState((prev) => ({ ...prev, scrollPosition: newPosition }));
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  // Calculate scroll progress (0-100)
  const getScrollProgress = useCallback(() => {
    const maxScroll = getMaxScroll();
    if (maxScroll === 0) return 0;
    return Math.min(100, (state.scrollPosition / maxScroll) * 100);
  }, [getMaxScroll, state.scrollPosition]);

  const getIsAtEnd = useCallback(() => {
    const maxScroll = getMaxScroll();
    if (maxScroll <= 0) return false; // 未初始化時不視為結束
    return state.scrollPosition >= maxScroll - 5;
  }, [getMaxScroll, state.scrollPosition]);

  return {
    state,
    containerRef,
    preset,
    wordCount,
    estimatedTotalTime: adjustedReadingTime,
    remainingTime,
    scrollProgress: getScrollProgress(),
    isAtEnd: getIsAtEnd(),
    // Expose scrollPosition directly for child components
    scrollPosition: state.scrollPosition,
    togglePlay,
    setSpeed,
    adjustSpeed,
    setFontSize,
    adjustFontSize,
    toggleMirrorMode,
    setScrollPosition,
    setElapsedTime,
    jumpTime,
    reset,
    manualScroll,
    showControlsTemporarily,
    hideControls,
    toggleFullscreen,
    syncScrollPosition,
    toggleControlsPinned,
  };
}
